import { useTranslation } from "react-i18next";
import { useIntl } from "react-intl";
import { getColumnsOrganizations } from "./columns";
import { useMemo, useRef } from "react";
import { DataTable, FormGroupHeader, Pagination } from "@/components/shared";
import {
  PaginatedMainOrganizationsWithHierarchyDocument,
  PaginatedMainOrganizationsWithHierarchyQuery,
  PaginatedMainOrganizationsWithHierarchyQueryVariables,
  OrganizationEdge,
} from "@/graphql/__generated__/graphql";
import { ActionButtonConfig } from "@/components/shared/FormGroupHeader";
import { RefreshCcwIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface PaginationRef {
  triggerHandleFilterSubmit: (_newVariables: Partial<PaginatedMainOrganizationsWithHierarchyQueryVariables>) => void;
}

/**
 * OrganizationsComponent component for displaying the organizations table
 * @returns JSX element
 */
function OrganizationsComponent() {
  // Hooks
  const intl = useIntl();
  const { t } = useTranslation(["translation", "components", "tables", "common"]);
  const navigate = useNavigate();

  // Pagination config
  const paginationRef = useRef<PaginationRef | null>(null);
  const paginationName = "paginatedMainOrganizationsWithHierarchy";

  const columns = useMemo(() => getColumnsOrganizations(intl, t), [intl, t]);

  const actionButtons: ActionButtonConfig[] = [
    { text: "", icon: <RefreshCcwIcon />, actionFn: () => { paginationRef.current?.triggerHandleFilterSubmit({}); }, variant: "outline", size: "icon" },
    { text: t("admin.organizations.neworganization"), actionFn: () => { navigate("/admin/organizations/create"); }, variant: "default" }
  ];

  return (
    <div className="pr-4 pl-2">
      <FormGroupHeader
        actionButtons={actionButtons}
        title={t("admin.organizations.title")}
        description={t("admin.organizations.description")}
      />

      <Pagination<
        OrganizationEdge,
        PaginatedMainOrganizationsWithHierarchyQuery,
        PaginatedMainOrganizationsWithHierarchyQueryVariables
      >
        ref={paginationRef}
        props={{
          queryDocument: PaginatedMainOrganizationsWithHierarchyDocument,
          paginationName,
          fetchPolicy: "cache-and-network",
        }}
      >
        {(dataSlice, loading, displayAmount) => (
          <div className="flex flex-col gap-2">
            <DataTable
              headerStyling="font-normal text-sm"
              disableRowColour={true}
              loading={loading}
              displayAmount={displayAmount as number}
              data={dataSlice.map((edge) => edge.node) ?? []}
              columns={columns}
              className="max-h-[calc(100vh-20rem)]"
            />
          </div>
        )}
      </Pagination>
    </div>
  );
}

export default OrganizationsComponent;
